import React from "react";
import HtmlText from "components/HtmlText";
import { findDictionaryItemByValue, getDefaultUnitsPrefix, recipeParameterUnitsType, } from "shared/rsDictionary";
import { fromToValidator, isNotEmpty } from "utils/validate";
import { capitalizeFirstLetter, renderNumberValue } from "utils/string";
import { ComplexValueParameterType, NoUnitsType, } from "store/recipe/recipe.types";
import { convertValueBasedOnUnits } from "utils/final-form-mutators";
import * as styles from "./styles.module.scss";
export const getInfiniteLabel = (props) => {
    const label = `${props?.withPrefix ? "for " : ""}the duration of culture`;
    return props?.capitalizeFirstLetter ? capitalizeFirstLetter(label) : label;
};
export const getComplexValueStepText = ({ value, parameters, prefix, customVariables, valueSuffix, validateValueFn, infiniteProps, }) => {
    switch (value?.type) {
        case ComplexValueParameterType.INFINITE:
            return (React.createElement(React.Fragment, null,
                prefix,
                prefix && React.createElement("span", null, " "),
                React.createElement("span", null, getInfiniteLabel(infiniteProps))));
        case ComplexValueParameterType.VALUES:
        case ComplexValueParameterType.ANY_UNIT_VALUE: {
            const { value: absoluteValue, units } = value;
            const unitsLabel = units !== NoUnitsType.NoUnits
                ? findDictionaryItemByValue(recipeParameterUnitsType, units, getDefaultUnitsPrefix)?.label
                : undefined;
            if (isNotEmpty(absoluteValue, true) &&
                (!validateValueFn || validateValueFn(absoluteValue)) &&
                (unitsLabel || units === NoUnitsType.NoUnits)) {
                return (React.createElement(React.Fragment, null,
                    typeof prefix === "function" ? prefix(absoluteValue) : prefix,
                    prefix && React.createElement("span", null, " "),
                    React.createElement(HtmlText, { text: `${renderNumberValue(absoluteValue)}${unitsLabel ? ` ${unitsLabel}` : ""}` }),
                    valueSuffix && React.createElement("span", null, " "),
                    typeof valueSuffix === "function" ? valueSuffix(absoluteValue) : valueSuffix));
            }
            return;
        }
        case ComplexValueParameterType.RELATIVE_VALUES: {
            const { value: relativeValue, of } = value;
            if (isNotEmpty(relativeValue) && (!validateValueFn || validateValueFn(relativeValue)) && of) {
                return (React.createElement(React.Fragment, null,
                    typeof prefix === "function" ? prefix(relativeValue) : prefix,
                    prefix && React.createElement("span", null, " "),
                    React.createElement(HtmlText, { text: `${renderNumberValue(relativeValue)}% of ${of.displayName}` })));
            }
            return;
        }
        case ComplexValueParameterType.PARAMETER: {
            const parameter = parameters?.find(({ id }) => id === value.parameterId);
            const parameterUnitsLabel = parameter?.units && parameter?.units !== NoUnitsType.NoUnits
                ? findDictionaryItemByValue(recipeParameterUnitsType, parameter?.units, getDefaultUnitsPrefix)
                    ?.label
                : undefined;
            if (isNotEmpty(parameter?.value, true) &&
                (!validateValueFn || validateValueFn(parameter?.value)) &&
                parameter?.name) {
                return (React.createElement(React.Fragment, null,
                    prefix,
                    prefix && React.createElement("span", null, " "),
                    React.createElement(HtmlText, { text: `{${parameter.name}:
              ${renderNumberValue(parameter?.value)}
              ${parameterUnitsLabel ? ` ${parameterUnitsLabel}` : ""}}` }),
                    valueSuffix && React.createElement("span", null, " "),
                    typeof valueSuffix === "function" ? valueSuffix(parameter?.value) : valueSuffix));
            }
            return;
        }
        case ComplexValueParameterType.CUSTOM_VARIABLE: {
            const customVariable = customVariables?.find(({ id }) => id === value.variableId);
            if (customVariable?.name) {
                return (React.createElement(React.Fragment, null,
                    prefix,
                    prefix && React.createElement("span", null, " "),
                    React.createElement(HtmlText, { text: `{${customVariable.name}}` })));
            }
            return;
        }
        default:
            return;
    }
};
const getComplexValueParameterValueAndUnits = (value, parameters) => {
    switch (value?.type) {
        case ComplexValueParameterType.ANY_UNIT_VALUE:
        case ComplexValueParameterType.VALUES:
            return { value: value.value, units: value.units };
        case ComplexValueParameterType.PARAMETER: {
            const selectedParameter = parameters?.find(({ id }) => id === value.parameterId);
            if (selectedParameter) {
                return { value: selectedParameter.value, units: selectedParameter.units };
            }
        }
    }
};
export const complexValueParameterFromToValidation = (recipeParameters, canBeEqual, customErrorMessage) => (values) => {
    const { min, max } = { ...values };
    if (!isNotEmpty(min) || !isNotEmpty(max)) {
        return;
    }
    const minValueAndUnits = getComplexValueParameterValueAndUnits(values?.min, recipeParameters);
    const maxValueAndUnits = getComplexValueParameterValueAndUnits(values?.max, recipeParameters);
    if (minValueAndUnits?.value && maxValueAndUnits?.value) {
        let error;
        if (minValueAndUnits.units && maxValueAndUnits.units) {
            const maxValue = convertValueBasedOnUnits(maxValueAndUnits.value, maxValueAndUnits.units, minValueAndUnits.units);
            error = fromToValidator(minValueAndUnits.value, maxValue, canBeEqual, customErrorMessage);
        }
        else {
            error = fromToValidator(minValueAndUnits.value, maxValueAndUnits.value, canBeEqual, customErrorMessage);
        }
        return error ? { minMax: error } : undefined;
    }
};
export const getTypeGroupOptions = ({ hideParameterType, hideRelativeType, hideAbsoluteType, hideCustomVariableType, withInfinite, isAnyUnitValue, readonly, }) => [
    ...(withInfinite
        ? [
            {
                value: ComplexValueParameterType.INFINITE,
                label: "∞",
                disabled: readonly,
                title: getInfiniteLabel({ withPrefix: true, capitalizeFirstLetter: true }),
                className: styles["typeGroupInput-button"],
            },
        ]
        : []),
    ...(!hideAbsoluteType
        ? [
            {
                value: isAnyUnitValue ? ComplexValueParameterType.ANY_UNIT_VALUE : ComplexValueParameterType.VALUES,
                label: "123",
                disabled: readonly,
                title: "Absolute value",
                className: styles["typeGroupInput-button"],
            },
        ]
        : []),
    ...(!hideRelativeType
        ? [
            {
                value: ComplexValueParameterType.RELATIVE_VALUES,
                label: "%",
                disabled: readonly,
                title: "Relative value",
                className: styles["typeGroupInput-button"],
            },
        ]
        : []),
    ...(!hideParameterType
        ? [
            {
                value: ComplexValueParameterType.PARAMETER,
                label: "{p}",
                disabled: readonly,
                title: "Recipe Parameter",
                className: styles["typeGroupInput-button"],
            },
        ]
        : []),
    ...(!hideCustomVariableType
        ? [
            {
                value: ComplexValueParameterType.CUSTOM_VARIABLE,
                label: "{v}",
                disabled: readonly,
                title: "Custom Variable",
                className: styles["typeGroupInput-button"],
            },
        ]
        : []),
];
